// src/components/CookieConsent.js

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MarketingLink } from './MarketingLink';
import { useCookieConsent } from '../context/CookieConsentContext';
import './CookieConsent.css';

const COOKIE_CONSENT_EXIT_MS = 380;

const CookieConsent = () => {
  const {
    showBanner,
    hasAcknowledged,
    acceptAdvertising,
    rejectOptional,
    closeCookieSettings,
  } = useCookieConsent();

  const [isExiting, setIsExiting] = useState(false);
  const exitTimerRef = useRef(null);

  useEffect(() => {
    if (showBanner) setIsExiting(false);
  }, [showBanner]);

  useEffect(() => () => {
    if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
  }, []);

  const isReopen = hasAcknowledged;

  const runDismiss = useCallback((action) => {
    if (isExiting) return;
    setIsExiting(true);
    exitTimerRef.current = window.setTimeout(() => {
      exitTimerRef.current = null;
      action();
      setIsExiting(false);
    }, COOKIE_CONSENT_EXIT_MS);
  }, [isExiting]);

  const handleAcceptAdvertising = useCallback(() => {
    runDismiss(acceptAdvertising);
  }, [runDismiss, acceptAdvertising]);

  const handleRejectOptional = useCallback(() => {
    runDismiss(() => {
      if (isReopen) closeCookieSettings();
      else rejectOptional();
    });
  }, [runDismiss, isReopen, closeCookieSettings, rejectOptional]);

  if (!showBanner && !isExiting) return null;

  return (
    <div
      className={`cookie-consent${isExiting ? ' cookie-consent--exit' : ''}`}
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-consent-title"
      aria-describedby="cookie-consent-desc"
    >
      <div className="cookie-consent-inner">
        <div className="cookie-consent-copy">
          <p className="cookie-consent-title" id="cookie-consent-title">
            Cookies &amp; privacy choices
          </p>
          <p className="cookie-consent-desc" id="cookie-consent-desc">
            We use essential cookies and browser storage for sign-in, security, and in-app
            preferences. With your permission, we also use Google Ads conversion measurement to
            understand which ads led to a subscription — no behavioural ad profiling on this site.
            See our{' '}
            <MarketingLink to="/privacy" className="cookie-consent-link">
              Privacy Policy
            </MarketingLink>
            .
          </p>
        </div>
        <div className="cookie-consent-actions">
          <button
            type="button"
            className="cookie-consent-btn cookie-consent-btn--primary"
            onClick={handleAcceptAdvertising}
            disabled={isExiting}
            aria-busy={isExiting}
          >
            Accept advertising
          </button>
          <button
            type="button"
            className="cookie-consent-btn cookie-consent-btn--close"
            onClick={handleRejectOptional}
            disabled={isExiting}
          >
            Essential only
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
